import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  PushDeviceService,
  PushDeviceStatus,
} from 'src/app/shared/services/push-device.service';

export type BrowserPermissionView = 'granted' | 'default' | 'denied' | 'unsupported';

@Component({
  selector: 'app-push-device-settings',
  templateUrl: './push-device-settings.component.html',
  styleUrls: ['./push-device-settings.component.scss'],
})
export class PushDeviceSettingsComponent implements OnInit, OnDestroy {
  /** Compact layout for header bell dropdown. */
  @Input() compact = false;

  enabled = false;
  loading = false;
  status: PushDeviceStatus | null = null;
  browserPermission: BrowserPermissionView = 'default';

  private statusSub?: Subscription;

  constructor(private readonly pushDevice: PushDeviceService) {}

  ngOnInit(): void {
    this.refreshBrowserPermission();
    this.statusSub = this.pushDevice.status$.subscribe((s) => {
      this.status = s;
      this.enabled = s?.notifications_enabled === true;
    });
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe();
  }

  async loadStatus(): Promise<void> {
    this.loading = true;
    try {
      await this.pushDevice.refreshStatus({ force: true });
      this.refreshBrowserPermission();
    } finally {
      this.loading = false;
    }
  }

  async onToggleChange(checked: boolean): Promise<void> {
    if (this.loading) {
      return;
    }
    this.loading = true;
    try {
      if (checked) {
        await this.pushDevice.enableForCurrentDevice();
      } else {
        await this.pushDevice.disableForCurrentDevice();
      }
      this.refreshBrowserPermission();
    } finally {
      this.loading = false;
    }
  }

  get deviceEnabled(): boolean {
    return this.enabled;
  }

  get browserBlocked(): boolean {
    return this.browserPermission === 'denied';
  }

  get browserNeedsPrompt(): boolean {
    return this.browserPermission === 'default';
  }

  get showBrowserWarning(): boolean {
    return this.browserBlocked || this.browserNeedsPrompt;
  }

  private refreshBrowserPermission(): void {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      this.browserPermission = 'unsupported';
      return;
    }
    this.browserPermission = Notification.permission as BrowserPermissionView;
  }
}
